import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Search, Loader2, ArrowRight, CornerDownLeft, Flame } from "lucide-react";

const QuickSearch = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [trending, setTrending] = useState([]);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/search?q=${encodeURIComponent(q)}&limit=8`);
        setResults(res.data.results || []);
        setActiveIndex(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    api.get("/search/trending?limit=5")
      .then((res) => setTrending(res.data.results || []))
      .catch(() => {});
  }, []);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const goTo = (item) => {
    api.post("/search/track", {
      category: item.category,
      brand: item.brand,
      model: item.model || "",
      label: item.label || `${item.brand} ${item.model || ""}`.trim(),
    }).catch(() => {});
    const params = new URLSearchParams({ category: item.category, brand: item.brand });
    if (item.model) params.set("model", item.model);
    navigate(`/valutazione?${params.toString()}`);
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) {
      if (e.key === "Enter" && q.trim()) {
        e.preventDefault();
        navigate(`/valutazione`);
      }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((i) => (i + 1) % results.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((i) => (i - 1 + results.length) % results.length); }
    else if (e.key === "Enter") { e.preventDefault(); goTo(results[activeIndex]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const showResults = open && q.trim().length >= 2;

  return (
    <div ref={containerRef} data-testid="quick-search" className="bg-white border border-black p-6 shadow-hard">
      <div className="flex items-center justify-between mb-4">
        <span className="overline">{t.landing.search_overline}</span>
        <span className="w-2 h-2 bg-[#FFCC00]" />
      </div>

      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none" />
        <input
          ref={inputRef}
          data-testid="quick-search-input"
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={t.landing.search_placeholder}
          className="input-rv pl-10 pr-10 text-base"
          autoComplete="off"
        />
        {loading && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[#737373]" />}
      </div>

      <p className="font-mono text-[10px] tracking-widest uppercase text-[#737373] mt-3">
        {t.landing.search_hint}
      </p>

      {showResults && (
        <div data-testid="quick-search-results" className="mt-4 border border-black/10 max-h-80 overflow-y-auto">
          {results.length === 0 && !loading && (
            <div className="p-4 font-mono text-xs text-[#737373]">{t.landing.search_empty}</div>
          )}
          {results.map((r, i) => {
            const catLabel = lang === "it" ? r.category_it : r.category_en;
            const isActive = i === activeIndex;
            return (
              <button
                key={`${r.category}-${r.brand}-${r.model}-${i}`}
                data-testid={`quick-search-result-${i}`}
                type="button"
                onClick={() => goTo(r)}
                onMouseEnter={() => setActiveIndex(i)}
                className={`w-full text-left px-4 py-3 border-b border-black/5 last:border-b-0 flex items-center justify-between transition-colors ${isActive ? "bg-[#FFCC00]/30" : "bg-white hover:bg-[#F5F5F5]"}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-sm truncate">
                    {r.brand}{r.model ? <span className="font-normal text-[#4A4A4A]"> · {r.model}</span> : null}
                  </div>
                  <div className="overline text-[#737373] mt-0.5 text-[9px]">{catLabel}</div>
                </div>
                <ArrowRight size={14} className={`flex-shrink-0 ${isActive ? "text-black" : "text-[#737373]"}`} />
              </button>
            );
          })}
        </div>
      )}

      {!showResults && trending.length > 0 && (
        <div data-testid="trending-searches" className="mt-4 border-t border-black/10 pt-4">
          <div className="flex items-center gap-1.5 mb-2">
            <Flame size={12} className="text-[#FF3B30]" />
            <span className="overline text-[9px]">{t.landing.trending_title}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {trending.map((item, i) => (
              <button
                key={`${item.category}-${item.brand}-${item.model}`}
                data-testid={`trending-item-${i}`}
                type="button"
                onClick={() => goTo(item)}
                className="font-mono text-[10px] tracking-wide px-2.5 py-1.5 border border-black bg-white hover:bg-[#FFCC00]/40 transition-colors"
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-black/10">
        <span className="font-mono text-[10px] text-[#737373] tracking-widest uppercase flex items-center gap-1">
          <CornerDownLeft size={11} /> {t.landing.search_enter}
        </span>
        <button
          data-testid="quick-search-skip"
          type="button"
          onClick={() => navigate("/valutazione")}
          className="font-mono text-[10px] font-bold tracking-widest uppercase underline hover:text-[#002FA7]"
        >
          {t.landing.search_skip}
        </button>
      </div>
    </div>
  );
};

export default QuickSearch;
